import { CloudUpload } from "lucide-react";
import { useRef, useState } from "react";

import { MediaGrid } from "@/components/project/MediaGrid";
import { UploadRow } from "@/components/project/UploadRow";
import { useUploads } from "@/lib/uploads";
import { cn } from "@/lib/utils";

const hasFiles = (e) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

export function MediaSection({ projectId, media, onReorder, onRemove }) {
  const uploads = useUploads(projectId);
  const input = useRef(null);
  const depth = useRef(0);
  const [over, setOver] = useState(false);

  const addFiles = (list) => {
    const files = Array.from(list ?? []);
    if (files.length) uploads.add(projectId, files);
  };
  const dnd = {
    onDragEnter: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setOver(true);
    },
    onDragOver: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (!depth.current) setOver(false);
    },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setOver(false);
      addFiles(e.dataTransfer.files);
    },
  };

  return (
    <section {...dnd} aria-labelledby="media-heading" data-testid="media-drop-area"
      className={cn("relative flex flex-col gap-4 rounded-[20px] transition-colors", over && "bg-primary/10 outline outline-2 outline-offset-8 outline-primary")}>
      <div className="flex items-baseline justify-between">
        <h2 id="media-heading" className="text-xl font-medium">Photos & videos</h2>
        <p className="text-[13px] text-muted-foreground" data-testid="media-count-text">{media.length} items</p>
      </div>
      <div className={cn("flex flex-col items-stretch gap-4 rounded-[20px] border-2 border-dashed border-accent/60 bg-primary/[0.06] p-4 sm:flex-row sm:items-center sm:p-5",
        over && "border-primary bg-primary/15")}>
        <button type="button" onClick={() => input.current?.click()} data-testid="upload-from-computer-button"
          className="focus-ring flex min-h-20 items-center gap-4 rounded-[16px] bg-primary px-6 text-left text-primary-foreground transition-all hover:brightness-110 active:scale-[0.99] sm:min-w-[300px]">
          <CloudUpload className="h-7 w-7 shrink-0" aria-hidden />
          <span className="flex flex-col">
            <span className="text-[17px] font-semibold">Upload from computer</span>
            <span className="text-[13px] opacity-80">Pick the videos and photos you shot</span>
          </span>
        </button>
        <p className="text-center text-sm text-card-foreground sm:text-left" data-testid="media-drop-hint">
          {over ? "Drop to upload" : "or drag and drop files here — large phone videos upload in resumable chunks."}
        </p>
        <input ref={input} type="file" multiple accept="image/*,video/*,.heic,.heif,.mov" className="sr-only" tabIndex={-1}
          aria-hidden data-testid="media-file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>
      {uploads.items.length ? (
        <ul className="flex flex-col gap-2" data-testid="upload-list">
          {uploads.items.map((u) => <UploadRow key={u.key} u={u} onRetry={() => uploads.retry(u.key)} onDismiss={() => uploads.dismiss(u.key)} />)}
        </ul>
      ) : null}
      {media.length > 1 ? <p className="text-[13px] text-muted-foreground">Drag the grip, or use the arrows, to set the video order.</p> : null}
      {media.length ? <MediaGrid media={media} onReorder={onReorder} onRemove={onRemove} /> : null}
    </section>
  );
}
